"use client";

import { motion } from "framer-motion";
import CountUp from "react-countup";

import RichTextContent from "@/components/sections/Content/RichTextContent";
import MasonryGallery from "@/components/sections/MasonryGallery";
import Title from "@/components/shared/Title";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/hooks/useTranslation";
import type { About } from "@/payload-types";
import type { PayLoadData } from "@/utils/payloadUtil";

export default function FamilyIntro({
  pageData,
}: {
  pageData: PayLoadData<About | undefined> | undefined;
}) {
  const { t } = useTranslation();
  const { language } = useLanguage();

  return (
    <section id="our-farm">
      <div className="container mx-auto px-4">
        <Title
          title={pageData?.[language]?.storyOfPensionHohenau.title || ""}
          desc=""
        />

        {/* --- */}
        <div className="flex flex-col gap-10">
          {/* box desc */}
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <div className="w-full flex-[0_0_auto]">
              <div className="rounded-sm bg-transparent p-0 shadow-sm md:bg-primary-500 md:px-16 md:py-14">
                {/* counter year */}
                {pageData?.[language]?.storyOfPensionHohenau?.numberCounter && (
                  <div className="mx-auto mb-6 flex w-fit items-center gap-3 rounded-full border border-primary-400 bg-primary-300 px-5 py-2 text-xl font-semibold text-white backdrop-blur-xl md:mx-0">
                    <div className="flex gap-1">
                      <CountUp
                        start={0}
                        end={
                          pageData?.[language]?.storyOfPensionHohenau
                            ?.numberCounter
                        }
                        duration={5}
                        suffix="+"
                      />
                    </div>
                    <div className="text-sm !leading-normal">
                      {t({
                        de: "Jahre des ErbesJahre des Erbes",
                        en: "Years of Heritage",
                      })}
                    </div>
                  </div>
                )}
                {/* content */}
                <div className="text-justify text-xl md:text-white">
                  <RichTextContent
                    data={
                      pageData?.[language]?.storyOfPensionHohenau.content ?? {
                        root: {
                          type: "root",
                          children: [],
                          direction: null,
                          format: "",
                          indent: 0,
                          version: 1,
                        },
                      }
                    }
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* gallery img */}
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <div className="w-full flex-1">
              <MasonryGallery
                masonryGallery={
                  pageData?.[language]?.storyOfPensionHohenau?.gallery
                }
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
